/**
 * Supabase client for the browser.
 *
 * flowType is 'implicit' (the supabase-js default).
 *
 * Why not PKCE: PKCE requires a codeVerifier stored in localStorage that's
 * generated when signInWithOtp() runs in the user's browser. Server-initiated
 * flows — Dashboard "Invite user", password recovery, the default Supabase
 * email templates — go through /auth/v1/verify on the server and emit a
 * `?code=` redirect. The browser has no matching codeVerifier, so
 * exchangeCodeForSession silently fails and the user is stuck in a loop.
 *
 * Implicit flow returns tokens in the URL fragment (#access_token=, #refresh_token=)
 * which detectSessionInUrl extracts without needing a codeVerifier. Tokens
 * appear briefly in the URL, which is the standard SPA tradeoff.
 *
 * If we ever move to SSR or want PKCE security, we'd also need to update
 * every Supabase email template to use {{ .TokenHash }} explicitly so the
 * callback can call verifyOtp without going through the server's verify
 * endpoint. AuthCallbackPage already handles that path for custom templates.
 */

import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // Don't throw — let the app mount so a friendly error screen can render.
  // eslint-disable-next-line no-console
  console.warn(
    '[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY missing — auth will not work.',
  );
}

export const supabase = createClient(url ?? '', anonKey ?? '', {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'implicit',
  },
});
